import { forwardRef, type ReactNode } from 'react'
import { cn } from '../utils/cn.ts'
import { Card, CardTitle, CardContent, CardAction } from '../components/Card.tsx'
import { Button } from '../components/Button.tsx'
import { ExpressPayButton } from '../components/ExpressPayButton.tsx'
import { PaymentDivider } from '../components/PaymentDivider.tsx'

export interface PaymentCardProps {
  amount: string
  feeLabel?: string
  onPay: () => void
  onApplePayClick?: () => void
  onGooglePayClick?: () => void
  error?: string
  loading?: boolean
  applePayAvailable?: boolean
  googlePayAvailable?: boolean
  stripeSlot?: ReactNode
  /** Show the top "{feeLabel} … {amount}" row. Turn off when an itemized
   *  summary in `stripeSlot` already carries a total, so the amount isn't
   *  shown twice. Defaults to true (back-compat). */
  showFeeRow?: boolean
  className?: string
}

export const PaymentCard = forwardRef<HTMLDivElement, PaymentCardProps>(
  (
    {
      amount,
      feeLabel = 'Event fee',
      onPay,
      onApplePayClick,
      onGooglePayClick,
      error,
      loading = false,
      applePayAvailable = false,
      googlePayAvailable = false,
      stripeSlot,
      showFeeRow = true,
      className,
    },
    ref,
  ) => {
    const showExpress = applePayAvailable || googlePayAvailable

    return (
      <Card ref={ref} className={cn('payment-card', className)}>
        <CardTitle>Complete your payment</CardTitle>
        <CardContent>
          <div className="payment-card-body">
          {showFeeRow && (
            <div className="payment-card-price-row">
              <span className="payment-card-price-label">{feeLabel}</span>
              <span className="payment-card-price-amount">{amount}</span>
            </div>
          )}

          {showExpress && (
            <div className="payment-card-express">
              {applePayAvailable && (
                <ExpressPayButton type="apple-pay" onClick={onApplePayClick} disabled={loading} />
              )}
              {googlePayAvailable && (
                <ExpressPayButton type="google-pay" onClick={onGooglePayClick} disabled={loading} />
              )}
            </div>
          )}

          {showExpress && <PaymentDivider variant="card" />}

          {stripeSlot && <div className="payment-card-stripe-slot">{stripeSlot}</div>}

          {error && <p className="payment-card-error">{error}</p>}
          </div>
        </CardContent>
        <CardAction>
          <Button loading={loading} onClick={onPay}>
            {error ? 'Try Again' : `Pay ${amount} & Confirm`}
          </Button>
        </CardAction>
      </Card>
    )
  },
)

PaymentCard.displayName = 'PaymentCard'
